import axios from 'axios';
import { isEmpty, map, sum } from 'lodash';
import moment from 'moment';
import { useContext, useEffect } from 'react';
import t from 'typy';
import { WrappedUseState, WrappedUseStateReturn } from '../functions/hooks';
import IsMountedWrapper from '../functions/isMountedWrapper';
import { convertToRezdyBooking, createRezdyPackageBooking } from '../functions/rezdy';
import { checkConversions } from '../functions/sales';
import { Booking, EMPTY_BOOKING, EMPTY_PACKAGE_BOOKING, GSLEntities, PaymentProvider } from '../models/bookings';
import { PriceOption } from '../models/products';
import { BaseStore, BaseStoreValues, WrappedSetter } from './BaseStore';
import { ProductsStore } from './ProductsStore';
import CustomerInfoStepController, { CustomerInfoStepControllerValue } from './bookingSections/CustomerInfoController';
import ExtraSelectionStepController, {
    ExtraSelectionStepControllerValue,
} from './bookingSections/ExtraSelectionStepController';
import ParticipantInfoStepController, {
    ParticipantInfoStepControllerValue,
} from './bookingSections/ParticipantInfoController';
import PickUpStepController, { PickUpStepControllerValue } from './bookingSections/PickUpStepController';
import { StepperStore, StepperStoreValue } from './bookingSections/StepperStore';
import { DefaultProviderProps, createStore, createStoreContext } from './common';

const apiURL = import.meta.env.PROD ? '/api/' : 'http://localhost:5000/api/';

class CardDetailKey {
    constructor(
        readonly Name?: string,
        // biome-ignore lint/suspicious/noShadowRestrictedNames: property name must match the payment API field "Number"
        readonly Number?: string,
        readonly ExpiryMonth?: string,
        readonly ExpiryYear?: string,
        readonly CVN?: string
    ) {
        this.Name = '';
    }
}

export interface CardDetails extends CardDetailKey {}

export interface PaymentObject {
    readonly cardDetails?: CardDetails;
    readonly paymentError?: boolean;
    readonly errorDetails?: any;
    readonly amount?: number;
    // Set when eWay declined with a code another acquirer may approve. The card fields
    // are swapped for Stripe's so the customer can simply try again.
    readonly fallbackActive?: boolean;
    readonly declineCode?: string;
}

// Runs the 3DS challenge Stripe asks for. Supplied by the component that owns the
// Stripe.js instance, since the store has no access to it.
export type ConfirmStripeAction = (clientSecret: string) => Promise<boolean>;

interface CompletedBookings {
    readonly booking: Booking;
    readonly package?: Booking;
}

export interface BookingStoreValue
    extends BaseStoreValues<Booking>,
        StepperStoreValue,
        CustomerInfoStepControllerValue,
        ExtraSelectionStepControllerValue,
        ParticipantInfoStepControllerValue,
        PickUpStepControllerValue {
    readonly booking: Booking;
    readonly setBooking: WrappedSetter<Booking>;
    readonly packageBooking: WrappedUseStateReturn<Booking>;
    readonly clearController: () => void;
    readonly payment: WrappedUseStateReturn<PaymentObject>;
    readonly completedBookings: WrappedUseStateReturn<CompletedBookings>;
    readonly updateCardDetails: (event: any, isEncrypted: boolean) => void;
    readonly clearCardDetails: () => void;
    readonly processBooking: () => void;
    readonly processStripeBooking: (paymentMethodId: string, confirmAction: ConfirmStripeAction) => Promise<void>;
    readonly handleQuantityChange: (event: any) => void;
    readonly calcBookingTotal: () => void;
    readonly checkFormErrors: (bookingStep: number) => boolean;
}

export const BookingStore = createStoreContext<BookingStoreValue>();

export const BookingStoreProvider = ({ children }: DefaultProviderProps) => {
    const base = BaseStore<Booking>(EMPTY_BOOKING);
    const isMounted = IsMountedWrapper();
    const productController = useContext(ProductsStore);
    const booking: Booking = base.mainObject;
    const packageBooking = WrappedUseState<Booking>(EMPTY_PACKAGE_BOOKING);
    const setBooking = base.setMainObject;
    const payment = WrappedUseState<PaymentObject>({});
    const encryptKey = import.meta.env.VITE_ENCRYPT_KEY;
    const localBookingString = import.meta.env.VITE_LOCAL_BOOKING;
    // Guarded for SSR/prerender: localStorage doesn't exist on the server.
    const cachedBooking = typeof window !== 'undefined' ? localStorage.getItem(localBookingString || '') : null;
    const existingBooking: Booking = cachedBooking ? JSON.parse(cachedBooking) : undefined;
    const completedBookings = WrappedUseState<CompletedBookings>({ booking: existingBooking || EMPTY_BOOKING });
    const stepper = useContext(StepperStore);
    const participantInfoStepController = ParticipantInfoStepController(setBooking, booking);
    const customerInfoStepController = CustomerInfoStepController(
        setBooking,
        booking,
        packageBooking.data,
        productController.availability
    );
    const extrasStepController = ExtraSelectionStepController(setBooking, booking, productController.product);
    const pickUpStepController = PickUpStepController(
        booking,
        setBooking,
        packageBooking.data,
        packageBooking.setData,
        productController.product,
        productController.packageProduct.data
    );

    const clearController = () => {
        setBooking(EMPTY_BOOKING, isMounted);
        packageBooking.setData(EMPTY_PACKAGE_BOOKING, isMounted);
        participantInfoStepController.setParticipantErrors([]);
        customerInfoStepController.setCustomerErrors({});
        payment.setData({}, isMounted);
    };

    const updateCardDetails = (event: any, isEncrypted: boolean) => {
        const key = event.target.name as string;
        const value = isEncrypted
            ? (window as any).eCrypt?.encryptValue(event.target.value, encryptKey)
            : (event.target.value as string);
        // encryptValue returns null/undefined when the CSE key (VITE_ENCRYPT_KEY) is
        // missing or the eCrypt script hasn't loaded - surface it instead of silently
        // submitting an empty value that eWAY rejects (V6022/V6126).
        if (isEncrypted && event.target.value && !value) {
            console.error(
                `[payment] Card field "${key}" failed to encrypt. Check VITE_ENCRYPT_KEY is set and eCrypt loaded.`
            );
        }
        payment.setData(
            (prev) => ({
                ...prev,
                cardDetails: {
                    ...prev.cardDetails,
                    [key]: value,
                },
            }),
            isMounted
        );
    };

    // Drop the eCrypted card values once eWay has declined. Nothing downstream can use
    // them - Stripe cannot read an eWay-encrypted card - so holding them serves no
    // purpose, and the customer is about to enter the card again.
    //
    // The cardholder name is kept: it is not card data, it is already on the booking as
    // the customer's name, and carrying it over means the retry form comes up prefilled
    // exactly as they left it rather than subtly re-worded.
    const clearCardDetails = () => {
        payment.setData(
            (prev) => ({
                ...prev,
                cardDetails: prev.cardDetails?.Name ? { Name: prev.cardDetails.Name } : undefined,
            }),
            isMounted
        );
    };

    const farePricing = (prices: PriceOption[]) => {
        const tempBooking = { ...booking };
        const newBooking: Booking = {
            ...tempBooking,
            quantities: (tempBooking?.quantities || []).map((q) => ({
                label: q?.label || '',
                price: (prices || []).find((po) => po?.label === q?.label)?.price || 0,
                value: q?.value || 0,
                seatsUsed: q?.seatsUsed || 1,
            })),
        };
        setBooking(newBooking, isMounted);
    };

    const bookingPrices = () => {
        const salesExist = (productController.product?.sales || []).length > 0;
        const currentSale = salesExist
            ? productController.product.sales.find((sale) =>
                  moment(booking?.tour?.startTimeLocal).isBetween(sale.startDate, sale.endDate)
              )
            : undefined;
        if (currentSale !== undefined) {
            farePricing(currentSale.fareTypes);
        } else {
            const departureTime = moment(booking?.tour?.startTimeLocal).toISOString();
            const isValid =
                (productController?.availability?.sessions || []).find((sesh) =>
                    moment(sesh.startTime).isSame(moment(departureTime))
                ) !== undefined;
            const priceOptions = isValid
                ? (productController.availability.sessions
                      .find((sesh) => moment(sesh.startTime).isSame(moment(departureTime)))
                      .priceOptions.map((po) => ({
                          label: po.label,
                          price: po.price,
                          seatsUsed: po.seatsUsed,
                      })) as PriceOption[])
                : productController.product.priceOptions;
            farePricing(priceOptions);
        }
    };

    const calcBookingTotal = () => {
        const fareTotal = sum(map(booking.quantities, (x) => x.price * x.value));
        const extrasTotal = booking.extras ? sum(map(booking.extras, (x) => (x.price * x.quantity) / 100)) : 0;
        setBooking(
            (prev) => ({
                ...prev,
                totalAmount: fareTotal + extrasTotal,
            }),
            isMounted
        );
        payment.setData(
            (prev) => ({
                ...prev,
                amount: (fareTotal + extrasTotal) * 100,
            }),
            isMounted
        );
    };

    useEffect(() => {
        base.setIsLoading(false, isMounted);
    }, []);

    useEffect(() => {
        calcBookingTotal();
    }, [booking.quantities, booking.extras, booking?.tour?.startTimeLocal]);

    useEffect(() => {
        bookingPrices();
    }, [booking?.tour?.startTimeLocal]);

    useEffect(() => {
        productController.product?.allowPackage &&
            packageBooking.setData(
                (prev) => ({
                    ...prev,
                    quantities: [
                        {
                            label: productController?.product.packageFare.label,
                            value: sum(booking.quantities.map((q) => q.value)),
                            seatsUsed: productController?.product.packageFare?.seatsUsed,
                        },
                    ],
                }),
                isMounted
            );
    }, [booking.quantities]);

    useEffect(() => {
        productController.product?.allowPackage &&
            packageBooking.setData(
                (prev) => ({
                    ...prev,
                    customer: booking.customer,
                }),
                isMounted
            );
    }, [booking.customer]);

    const handleQuantityChange = (event: any) => {
        const label = event.target.name;
        const quantity = Number(event.target.value);
        const index = productController.product.priceOptions.findIndex((x) => x.label.includes(label));
        const price = t(productController.product.priceOptions[index].price).safeNumber;
        const fareOption: PriceOption = {
            label: label,
            price: price,
            value: quantity,
        };
        const tempQuantities = booking.quantities ? booking.quantities : [];
        const replaceIndex = booking.quantities ? booking.quantities.findIndex((x) => x.label.includes(label)) : 0;
        replaceIndex === -1 ? tempQuantities.concat([fareOption]) : tempQuantities.splice(replaceIndex, 1, fareOption);
        setBooking(
            (prev) => ({
                ...prev,
                quantities: tempQuantities,
            }),
            isMounted
        );
    };

    // eWay and Stripe expose the same authorise/capture/cancel triple under different
    // prefixes, so the booking flow only needs to know which one holds the hold.
    const providerPath = (provider: PaymentProvider) => (provider === PaymentProvider.STRIPE ? 'stripe' : 'payments');

    // Returns the reference the payment finally settled under. eWay treats the capture as
    // a transaction of its own and issues a new id for it, and that is the only one it
    // will accept for a refund; Stripe keeps the same intent id throughout.
    const capturePayment = async (
        transID?: string,
        amount?: number,
        provider: PaymentProvider = PaymentProvider.EWAY,
        bookingRef?: string
    ): Promise<string | undefined> => {
        if (!transID) {
            return undefined;
        }
        try {
            const res = await axios.post(`${apiURL}${providerPath(provider)}/capture`, {
                transID,
                amount: amount ?? Math.round((booking.totalAmount || 0) * 100),
                entity: booking.entity,
                // Both only matter if something goes wrong: the booking is already
                // confirmed here, so a failed capture has to be chased up by hand.
                bookingRef,
                customer: booking.customer,
            });
            return res.data?.success && res.data?.transID ? String(res.data.transID) : undefined;
        } catch (e) {
            console.error('Failed to capture payment', e);
            return undefined;
        }
    };

    const cancelPayment = async (transID?: string, provider: PaymentProvider = PaymentProvider.EWAY) => {
        if (!transID) {
            return;
        }
        try {
            await axios.post(`${apiURL}${providerPath(provider)}/cancel`, {
                transID,
                entity: booking.entity,
            });
        } catch (e) {
            console.error('Failed to cancel payment authorisation', e);
        }
    };

    const submitBooking = async (
        bookingObject: Booking,
        isPackage?: boolean,
        transID?: string,
        provider: PaymentProvider = PaymentProvider.EWAY
    ) => {
        try {
            const res = await axios.post(
                `${apiURL}rezdy/bookings?entity=${bookingObject.entity}&provider=${provider}`,
                convertToRezdyBooking(bookingObject, productController.product)
            );
            if (res.data.success) {
                // Booking confirmed: capture the previously authorised payment.
                const capturedTransID = await capturePayment(
                    transID,
                    Math.round((bookingObject.totalAmount || 0) * 100),
                    provider,
                    res.data.refNum
                );
                // eWay's capture settles under a new reference, and a refund has to quote
                // that one rather than the authorisation. The server swaps it onto the
                // Rezdy booking; carrying it here keeps the confirmation page, the
                // confirmation email and any package leg quoting the same number.
                const settledBooking: Booking =
                    capturedTransID && capturedTransID !== transID
                        ? {
                              ...bookingObject,
                              payment: {
                                  ...bookingObject.payment,
                                  paymentId: capturedTransID,
                                  label: capturedTransID,
                              },
                          }
                        : bookingObject;
                try {
                    const packageRes =
                        isPackage &&
                        (await axios.post(
                            `${apiURL}rezdy/bookings?entity=${productController.packageProduct.data.entity}&provider=${provider}`,
                            createRezdyPackageBooking(
                                {
                                    ...settledBooking,
                                    bookingResponse: res.data,
                                },
                                packageBooking.data,
                                productController.product,
                                productController.packageProduct.data
                            )
                        ));
                    if (packageRes?.data?.success) {
                        packageBooking.setData(
                            (prev) => ({
                                ...prev,
                                bookingResponse: packageRes.data,
                                bookingComplete: true,
                            }),
                            isMounted
                        );
                    }
                } catch (e) {
                    console.log(e);
                    console.error(e);
                }
                setBooking(
                    (prev) => ({
                        ...prev,
                        payment: settledBooking.payment,
                        bookingResponse: res.data,
                        bookingComplete: true,
                    }),
                    isMounted
                );
                axios.post(`${apiURL}email/booking?entity=${GSLEntities.AVIATION}`, {
                    ...settledBooking,
                    packageBooking: packageBooking.data,
                    bookingResponse: res.data,
                });
                checkConversions({ ...settledBooking, bookingResponse: res.data });
            } else {
                // Booking rejected after authorisation: release the hold so the
                // customer is never charged. The server also notifies the res team.
                await cancelPayment(transID, provider);
                setBooking((prev) => ({ ...prev, bookingResponse: res.data }), isMounted);
            }
        } catch (e) {
            console.error(e);
            await cancelPayment(transID, provider);
            setBooking((prev) => ({ ...prev, bookingResponse: { success: false, error: e } }), isMounted);
        }
    };

    const flagPaymentError = (errorDetails?: any, declineCode?: string) => {
        payment.setData(
            (prev) => ({
                ...prev,
                paymentError: true,
                fallbackActive: false,
                declineCode,
                errorDetails,
            }),
            isMounted
        );
    };

    const buildPaymentRecord = (transID: string, amountInCents: number, provider: PaymentProvider) => ({
        amount: amountInCents / 100,
        paymentId: String(transID),
        // Stays the bare transaction reference: it's shown to the customer as
        // "Merchant ref" on the confirmation page. The provider goes in the notes.
        label: String(transID),
        date: moment().toISOString(),
        type: 'CREDITCARD',
        provider,
    });

    // eWay pre-auth flow: authorise -> create the Rezdy booking -> capture on success /
    // cancel the hold on failure so the customer is never charged for a failed booking.
    const processBooking = async () => {
        base.setIsLoading(true, isMounted);
        try {
            const response = await axios.post(`${apiURL}payments/authorise`, {
                ...payment.data,
                amount: Math.round((booking.totalAmount || 0) * 100),
                entity: booking.entity,
                customer: booking.customer,
            });
            if (response.data.success === true) {
                const paymentRecord = buildPaymentRecord(
                    response.data.transID,
                    response.data.amount,
                    PaymentProvider.EWAY
                );
                payment.setData(
                    (prev) => ({
                        ...prev,
                        paymentError: false,
                        fallbackActive: false,
                    }),
                    isMounted
                );
                setBooking((prev) => ({ ...prev, payment: paymentRecord }), isMounted);
                await submitBooking(
                    { ...booking, payment: paymentRecord },
                    booking.isPackage,
                    String(response.data.transID),
                    PaymentProvider.EWAY
                );
            } else if (response.data.retryable === true) {
                // Declined with a code another acquirer may approve. Drop the card data we
                // hold and let the customer re-enter it against Stripe instead.
                clearCardDetails();
                payment.setData(
                    (prev) => ({
                        ...prev,
                        paymentError: false,
                        fallbackActive: true,
                        declineCode: response.data.code,
                        errorDetails: response.data.errors,
                    }),
                    isMounted
                );
            } else {
                flagPaymentError(response.data.errors, response.data.code);
            }
        } catch (error) {
            // The request failed, so we don't know whether eWay authorised the card. Never
            // offer a retry here, or the customer could be left with two holds.
            console.error(error);
            flagPaymentError(error);
        } finally {
            base.setIsLoading(false, isMounted);
        }
    };

    // Second attempt, against Stripe, after eWay declined a card it may not have been
    // able to approve. Reuses submitBooking so the Rezdy booking, package booking,
    // confirmation email and conversion tracking all behave exactly as before.
    const processStripeBooking = async (paymentMethodId: string, confirmAction: ConfirmStripeAction) => {
        base.setIsLoading(true, isMounted);
        try {
            const amountInCents = Math.round((booking.totalAmount || 0) * 100);
            const response = await axios.post(`${apiURL}stripe/authorise`, {
                paymentMethodId,
                amount: amountInCents,
                entity: booking.entity,
                customer: booking.customer,
                reference: booking.tour?.productCode,
                // Recorded against the intent so a recovered sale can report what eWay
                // declined it with.
                ewayCode: payment.data.declineCode,
            });

            let authorised = response.data.success === true;
            let authorisedAmount = response.data.amount;
            const transID = response.data.transID;

            // 3D Secure: Stripe asks for a challenge the eWay MOTO path never triggered.
            // Only treat the payment as authorised once the server confirms the hold.
            if (!authorised && response.data.requiresAction && response.data.clientSecret) {
                const completed = await confirmAction(response.data.clientSecret);
                if (completed) {
                    const verified = await axios.post(`${apiURL}stripe/verify`, {
                        transID,
                        entity: booking.entity,
                    });
                    authorised = verified.data.success === true;
                    authorisedAmount = verified.data.amount ?? authorisedAmount;
                }
            }

            if (authorised) {
                const paymentRecord = buildPaymentRecord(
                    transID,
                    authorisedAmount ?? amountInCents,
                    PaymentProvider.STRIPE
                );
                payment.setData(
                    (prev) => ({
                        ...prev,
                        paymentError: false,
                        fallbackActive: false,
                    }),
                    isMounted
                );
                setBooking((prev) => ({ ...prev, payment: paymentRecord }), isMounted);
                await submitBooking(
                    { ...booking, payment: paymentRecord },
                    booking.isPackage,
                    String(transID),
                    PaymentProvider.STRIPE
                );
            } else {
                // Both providers declined, so this is very likely a genuine decline.
                flagPaymentError(response.data.errors, response.data.code);
            }
        } catch (error) {
            console.error(error);
            flagPaymentError(error);
        } finally {
            base.setIsLoading(false, isMounted);
        }
    };

    const checkFormErrors = (bookingStep: number) => {
        switch (bookingStep) {
            case 0:
                return !customerInfoStepController.isCustomerValid();
            case 1:
                return !participantInfoStepController.isParticipantInfoStepValid();
            default:
                return false;
        }
    };

    // Default the first participant (pax 1) to the lead customer's name, since they're
    // the same person ~99% of the time. Only fills empty fields so user edits and
    // additional participants (pax 2, 3...) are left untouched.
    const applyLeadToFirstParticipant = () => {
        setBooking((prev) => {
            const participants = prev.participants || [];
            if (participants.length === 0) {
                return prev;
            }
            const leadValueForLabel = (label?: string) => {
                const normalized = (label || '').trim().toLowerCase();
                if (normalized === 'first name') {
                    return prev.customer?.firstName;
                }
                if (normalized === 'last name' || normalized === 'surname') {
                    return prev.customer?.lastName;
                }
                return undefined;
            };
            const firstParticipantFields = (participants[0]?.fields || []).map((field) => {
                const leadValue = leadValueForLabel(field.label);
                return leadValue && isEmpty(field.value) ? { ...field, value: leadValue } : field;
            });
            return {
                ...prev,
                participants: participants.map((participant, index) =>
                    index === 0 ? { ...participants[0], fields: firstParticipantFields } : participant
                ),
            };
        }, isMounted);
    };

    const nextStep = () => {
        if (stepper.step === 0) {
            applyLeadToFirstParticipant();
        }
        stepper.nextStep();
    };

    return createStore(BookingStore, children, {
        ...base,
        ...stepper,
        ...extrasStepController,
        ...participantInfoStepController,
        ...customerInfoStepController,
        ...pickUpStepController,
        booking,
        packageBooking,
        nextStep,
        checkFormErrors,
        completedBookings,
        setBooking,
        calcBookingTotal,
        handleQuantityChange,
        clearController,
        updateCardDetails,
        clearCardDetails,
        payment,
        processBooking,
        processStripeBooking,
    });
};
